import { useCallback, useEffect, useState } from "react";
import useIsMobile from "../../hooks/useIsMobile";

const COMPACT_PICKER_MEDIA = "(max-width: 1200px)";

export const useReceptionistMobilePickerProps = ({ range = false } = {}) => {
  const isMobile = useIsMobile();
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(COMPACT_PICKER_MEDIA).matches;
  });
  const [forceClosed, setForceClosed] = useState(false);

  const handleOpenChange = useCallback((nextOpen) => {
    if (nextOpen) setForceClosed(false);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_PICKER_MEDIA);
    const onChange = () => setIsCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    if (!isCompact || !forceClosed) return undefined;
    const release = (event) => {
      if (event.target?.closest?.(".ant-picker")) setForceClosed(false);
    };
    document.addEventListener("pointerdown", release, true);
    document.addEventListener("touchstart", release, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener("pointerdown", release, true);
      document.removeEventListener("touchstart", release, { capture: true });
    };
  }, [isCompact, forceClosed]);

  useEffect(() => {
    if (!isCompact || forceClosed) return undefined;

    const close = (event) => {
      if (event.target?.closest?.(".ant-picker-dropdown")) return;
      setForceClosed(true);
    };

    const opts = { capture: true, passive: true };
    window.addEventListener("touchmove", close, opts);
    window.addEventListener("wheel", close, opts);
    document.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("touchmove", close, { capture: true });
      window.removeEventListener("wheel", close, { capture: true });
      document.removeEventListener("scroll", close, true);
    };
  }, [isCompact, forceClosed]);

  if (!isCompact) return {};

  const common = {
    open: forceClosed ? false : undefined,
    onOpenChange: handleOpenChange,
    transitionName: "",
  };

  if (range) {
    return {
      ...common,
      placement: "bottomRight",
      popupClassName: "curengo-mobile-picker curengo-range-picker",
      popupStyle: {
        maxWidth: isMobile
          ? "calc(100vw - 16px)"
          : "min(360px, calc(100vw - 24px))",
      },
    };
  }

  return {
    ...common,
    getPopupContainer: (trigger) => {
      const host = trigger?.closest(".ant-form-item") || trigger?.parentElement;
      if (!host) return document.body;
      if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      return host;
    },
    placement: "bottomLeft",
    popupClassName: "curengo-mobile-picker curengo-inflow-picker",
    popupStyle: {
      maxWidth: isMobile
        ? "calc(100vw - 16px)"
        : "min(360px, calc(100vw - 24px))",
    },
  };
};

export default useReceptionistMobilePickerProps;
